import { DialogActions, DialogContent } from '@material-ui/core';
import { find, last, sortBy, values } from 'lodash-es';
import React, { useState } from 'react';
import { useBoolean } from 'react-use-boolean';
import { PanoramaMonoSvg } from '../../assets/img/PanoramaMonoSvg';
import { useQueryFolders } from '../../hooks/folderQueries';
import { Alert } from '../common/Alert';
import { Button, CancelButton, CloseButton, ConfirmButton, GoBackButton } from '../common/Button';
import { Dialog, DialogTitle } from '../common/Dialog';
import { Loader } from '../common/Loader';
import { Typography } from '../common/Typography';
import {
  Content,
  ContentDetails,
  Folder,
  isFolderContent,
  isPanoramaDetails,
  PanoramaDetails,
  RootFolder,
} from '../services/UploadManagementServiceTypes';
import { OpenEditorMessage } from './GalleryContext';
import { PanoramaGallerySelectView } from './PanoramaGallerySelectView';

export interface CreateMonoPanoramaGalleryProps {
  currentFolder: RootFolder | Folder;
}

export const CreateMonoPanoramaGallery: React.FC<CreateMonoPanoramaGalleryProps> = ({
  currentFolder,
}) => {
  const [open, actions] = useBoolean();
  return (
    <>
      <CreateMonoPanoramaGalleryButton onClick={actions.on} />
      <Dialog open={open} onClose={actions.off}>
        <DialogTitle>Select Panoramas</DialogTitle>
        <CreateMonoPanoramaGalleryModal
          minNumberOfSelection={2}
          descriptionText='Select at least 2 panoramas exported from the same project.'
          initialPanoramas={[]}
          currentFolder={currentFolder}
          onClose={actions.off}
          onConfirm={(newPanoramas) => {
            actions.off();
            const payload: OpenEditorMessage = { newPanoramas, currentFolder };
            sessionStorage.setItem('newGallery', JSON.stringify(payload));
            window.open(window.location.origin + '/account#!/panoramaGallery/editor/');
          }}
          confirmText='Start Editing'
        />
      </Dialog>
    </>
  );
};

interface CreateMonoPanoramaGalleryButtonProps {
  onClick: () => void;
}

const CreateMonoPanoramaGalleryButton: React.FC<CreateMonoPanoramaGalleryButtonProps> = ({
  onClick,
}) => (
  <Button
    variant='outlined'
    startIcon={<PanoramaMonoSvg height={20} width={20} />}
    onClick={onClick}
  >
    Create Mono Panorama Gallery
  </Button>
);

export interface CreateMonoPanoramaGalleryModalProps extends CreateMonoPanoramaGalleryProps {
  onClose: () => void;
  onConfirm: (selectedPanoramas: PanoramaDetails[]) => void;
  confirmText: string;
  initialPanoramas?: PanoramaDetails[];
  descriptionText: string;
  minNumberOfSelection: number;
}

export const CreateMonoPanoramaGalleryModal: React.FC<CreateMonoPanoramaGalleryModalProps> = ({
  minNumberOfSelection,
  descriptionText,
  currentFolder,
  onClose,
  onConfirm,
  confirmText,
  initialPanoramas = [],
}) => {
  const [{ openedFolderGuid, previousOpenedFolderGuids, selectedPanoramas }, setState] = useState<{
    previousOpenedFolderGuids: Array<string | null | undefined>;
    openedFolderGuid?: string | null;
    selectedPanoramas: PanoramaDetails[];
  }>({
    openedFolderGuid: currentFolder.guid,
    selectedPanoramas: [],
    previousOpenedFolderGuids: [],
  });
  const canGoToPrevious = previousOpenedFolderGuids.length > 0;

  const { isLoading, isError, data } = useQueryFolders(openedFolderGuid);
  const { folderLike, contentDetails } = data || {};

  const getProjectName = () => {
    if (initialPanoramas?.length) {
      return initialPanoramas[0].projectName;
    }

    if (!selectedPanoramas.length || !contentDetails) {
      return undefined;
    }
    return selectedPanoramas[0].projectName;
  };

  const goToFolder = (openedFolderGuid?: string | null) => {
    setState((prevState) => ({
      ...prevState,
      openedFolderGuid,
      previousOpenedFolderGuids: prevState.previousOpenedFolderGuids.concat(
        prevState.openedFolderGuid,
      ),
    }));
  };

  const goToPreviousFolder = () => {
    setState((prevState) => {
      const openedFolderGuid = last(prevState.previousOpenedFolderGuids);
      const previousOpenedFolderGuids = prevState.previousOpenedFolderGuids.slice(0, -1);
      return {
        ...prevState,
        openedFolderGuid,
        previousOpenedFolderGuids,
      };
    });
  };

  const updateSelectedPanoramas = (panorama: PanoramaDetails) => {
    setState((prevState) => {
      const foundPanorama = prevState.selectedPanoramas.find(
        (p) => p.panoramaGuid === panorama.panoramaGuid,
      );
      const selectedPanoramas = foundPanorama
        ? prevState.selectedPanoramas.filter((p) => p !== foundPanorama)
        : prevState.selectedPanoramas.concat(panorama);
      return {
        ...prevState,
        selectedPanoramas,
      };
    });
  };

  if (isLoading) {
    return (
      <DialogContent>
        <Loader />
      </DialogContent>
    );
  }

  if (isError || !folderLike || !contentDetails) {
    return (
      <>
        <DialogContent>
          {isError ? (
            <Alert severity='error' shown={true} text={"The folder couldn't be loaded."} />
          ) : (
            <Typography variant={'h5'}>Folder not found</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CloseButton onClick={onClose} />
          {canGoToPrevious && <GoBackButton goBack={goToPreviousFolder} />}
        </DialogActions>
      </>
    );
  }

  // NOTE: the title from the panorama metadata does not correspond to the name of the corresponding content file
  const getPanoramaDetailsWithTitles = (
    contentDetails: ContentDetails,
    contents: Content[],
  ): PanoramaDetails[] => {
    const panoramas = values(contentDetails).filter(
      (detail): detail is PanoramaDetails => isPanoramaDetails(detail) && !detail.isStereo,
    );
    const withTitles = panoramas.map((panoramaDetails) => {
      const title =
        find(contents, { guid: panoramaDetails.panoramaGuid })?.name || panoramaDetails.title;
      return { ...panoramaDetails, title };
    });
    return sortBy(withTitles, (panoramaDetails) => panoramaDetails.title);
  };
  return (
    <>
      <DialogContent>
        <Typography paragraph>{descriptionText}</Typography>
        <PanoramaGallerySelectView
          panoramaGuidsInGallery={initialPanoramas.map((p) => p.panoramaGuid)}
          currentFolder={folderLike}
          navigateToFolder={goToFolder}
          selectedPanoramaGuids={selectedPanoramas.map((p) => p.panoramaGuid)}
          updateSelectedPanoramas={updateSelectedPanoramas}
          projectName={getProjectName()}
          folders={sortBy(
            folderLike.contents.filter(isFolderContent),
            (folderContent) => folderContent.name,
          )}
          panoramas={getPanoramaDetailsWithTitles(contentDetails, folderLike.contents)}
        />
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <ConfirmButton
          onClick={() => onConfirm(selectedPanoramas)}
          children={confirmText}
          disabled={selectedPanoramas.length < minNumberOfSelection}
        />
      </DialogActions>
    </>
  );
};
